import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { HiOutlineX, HiOutlineDocumentText } from 'react-icons/hi';
import { API_ENDPOINTS } from '../config/api';

function formatEffectiveDate(effectiveAt) {
  if (!effectiveAt) return '';
  try {
    const d = new Date(effectiveAt);
    if (isNaN(d.getTime())) return String(effectiveAt);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch {
    return String(effectiveAt);
  }
}

/**
 * 서비스 이용약관 조회 모달.
 * 목록 순서(시행일 내림차순): 0번이 최신.
 * 이전 개정 = 과거로 한 단계, 이후 개정 = 최신 쪽으로 한 단계.
 */
const ServiceTermsViewModal = ({ visible, onClose }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [versions, setVersions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [content, setContent] = useState('');
  const [versionName, setVersionName] = useState('');
  const [effectiveAt, setEffectiveAt] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    if (visible) {
      setCurrentIndex(0);
      setVersions([]);
      setContent('');
      setVersionName('');
      setEffectiveAt(null);
      setError(null);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;

    let cancelled = false;

    async function loadList() {
      setListLoading(true);
      setError(null);
      try {
        const vRes = await fetch(API_ENDPOINTS.SERVICE_TERMS_VERSIONS_PUBLIC);
        const raw = vRes.ok ? await vRes.json() : [];
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        setVersions(list);
        if (!list.length) {
          setError('등록된 서비스 이용약관이 없습니다.');
          setContent('');
          setVersionName('');
          setEffectiveAt(null);
        }
      } catch (e) {
        if (!cancelled) setError('목록을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setListLoading(false);
      }
    }

    loadList();
    return () => { cancelled = true; };
  }, [visible]);

  useEffect(() => {
    if (!visible || versions.length === 0) return undefined;

    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      setError(null);
      try {
        const idx = Math.min(Math.max(0, currentIndex), versions.length - 1);
        const row = versions[idx];
        if (!row?.id) {
          if (!cancelled) setError('약관 정보를 불러올 수 없습니다.');
          return;
        }

        const detailRes = await fetch(API_ENDPOINTS.SERVICE_TERMS_PUBLIC(row.id));
        if (!detailRes.ok) {
          if (!cancelled) {
            setError(detailRes.status === 404 ? '해당 버전을 찾을 수 없습니다.' : '내용을 불러오지 못했습니다.');
            setContent('');
            setVersionName('');
            setEffectiveAt(null);
          }
          return;
        }

        const data = await detailRes.json();
        if (cancelled) return;
        setContent(data.contentFull || '');
        setVersionName(data.versionName || '');
        setEffectiveAt(data.effectiveAt || null);
      } catch (e) {
        if (!cancelled) setError('내용을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }

    loadDetail();
    return () => { cancelled = true; };
  }, [visible, currentIndex, versions]);

  const loading = listLoading || detailLoading;
  const canGoOlder = versions.length > 1 && currentIndex < versions.length - 1;
  const canGoNewer = currentIndex > 0;

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-terms-modal-title"
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <HiOutlineDocumentText className="text-gray-600 shrink-0" />
            <h2 id="service-terms-modal-title" className="text-lg font-semibold text-gray-900 truncate">
              서비스 이용약관
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 shrink-0"
            aria-label="닫기"
          >
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-100 flex-shrink-0 text-sm text-gray-500">
          {versionName && <span>버전: {versionName}</span>}
          {versionName && effectiveAt && ' · '}
          {effectiveAt && <span>시행일: {formatEffectiveDate(effectiveAt)}</span>}
          {versions.length > 1 && (
            <span className="text-gray-400 ml-2">
              ({currentIndex + 1} / {versions.length})
            </span>
          )}
        </div>

        {!loading && !error && versions.length > 1 && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => canGoOlder && setCurrentIndex((i) => i + 1)}
                disabled={!canGoOlder}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                이전 개정
              </button>
              <button
                type="button"
                onClick={() => canGoNewer && setCurrentIndex((i) => i - 1)}
                disabled={!canGoNewer}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                이후 개정
              </button>
            </div>
            <p className="text-xs text-gray-500">
              이전: 더 과거 약관 · 이후: 더 최신 약관
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          )}
          {error && <p className="text-red-600 py-4 text-sm">{error}</p>}
          {!loading && !error && content && (
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
              {content}
            </pre>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceTermsViewModal;
